import React, { useState, useEffect } from 'react';
import axios from '../utils/authInterceptor';
import InvestorMetrics from './InvestorMetrics';

/**
 * System Admin Dashboard
 *
 * Internal dashboard for platform operators (system_admin role).
 * Displays investor metrics, platform health, and administrative functions.
 */
const SystemAdminDashboard = ({ user, onLogout }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/investor/dashboard');
      setMetrics(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setError(err.response?.data?.error || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get('/api/admin/investor/export', {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `investor_metrics_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export metrics');
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const MetricCard = ({ title, value, subtitle, color = '#6366f1' }) => (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '1.5rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb'
    }}>
      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
        {title}
      </div>
      <div style={{ fontSize: '2rem', fontWeight: '700', color }}>
        {value}
      </div>
      {subtitle && (
        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
          {subtitle}
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f3f4f6',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
    }}>
      {/* Header */}
      <header style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
        color: 'white',
        padding: '1rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
            System Admin Dashboard
          </h1>
          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', opacity: 0.8 }}>
            Platform metrics & investor reporting
          </p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.875rem', opacity: 0.8 }}>
            {user?.username}
          </span>
          <button
            onClick={onLogout}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            Logout
          </button>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '0 2rem',
        display: 'flex',
        gap: '2rem'
      }}>
        {['investor', 'overview', 'funnel', 'users', 'subscriptions', 'schools'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              background: 'none',
              border: 'none',
              padding: '1rem 0',
              fontSize: '0.875rem',
              fontWeight: activeTab === tab ? '600' : '400',
              color: activeTab === tab ? '#4f46e5' : '#6b7280',
              borderBottom: activeTab === tab ? '2px solid #4f46e5' : '2px solid transparent',
              cursor: 'pointer',
              textTransform: 'capitalize'
            }}
          >
            {tab}
          </button>
        ))}
      </nav>

      {/* Main Content */}
      <main style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Actions Bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1f2937' }}>
            {activeTab === 'investor' ? 'Investor Metrics' :
             activeTab === 'overview' ? 'Platform Overview' :
             activeTab === 'funnel' ? 'Conversion Funnel' :
             activeTab === 'users' ? 'User Metrics' :
             activeTab === 'subscriptions' ? 'Subscription Metrics' : 'School Licenses'}
          </h2>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={fetchMetrics}
              style={{
                background: 'white',
                border: '1px solid #d1d5db',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Refresh
            </button>
            <button
              onClick={handleExport}
              style={{
                background: '#4f46e5',
                border: 'none',
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #e5e7eb',
              borderTopColor: '#4f46e5',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <p style={{ color: '#6b7280' }}>Loading metrics...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '1rem',
            color: '#dc2626',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {/* Investor tab — self-fetches its own data; bypasses the
            shared `metrics` state used by the other tabs. */}
        {activeTab === 'investor' && <InvestorMetrics />}

        {/* Metrics Display */}
        {!loading && metrics && activeTab !== 'investor' && (
          <>
            {activeTab === 'overview' && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem'
              }}>
                <MetricCard
                  title="Total Users"
                  value={formatNumber(metrics.users?.total)}
                  subtitle={`+${metrics.users?.new_last_7_days || 0} this week`}
                />
                <MetricCard
                  title="Active Subscriptions"
                  value={formatNumber(metrics.subscriptions?.active)}
                  color="#10b981"
                />
                <MetricCard
                  title="Monthly Revenue"
                  value={`$${formatNumber(metrics.subscriptions?.mrr || 0)}`}
                  subtitle="MRR"
                  color="#f59e0b"
                />
                <MetricCard
                  title="School Licenses"
                  value={formatNumber(metrics.schools?.total_licenses)}
                  subtitle={`${metrics.schools?.total_seats || 0} total seats`}
                  color="#8b5cf6"
                />
                <MetricCard
                  title="Active Today"
                  value={formatNumber(metrics.engagement?.active_today)}
                />
                <MetricCard
                  title="Sessions (7d)"
                  value={formatNumber(metrics.engagement?.sessions_last_7_days)}
                />
              </div>
            )}

            {activeTab === 'funnel' && (
              <div>
                {/* Funnel Visualization */}
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '2rem',
                  marginBottom: '1.5rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{ margin: '0 0 1.5rem', color: '#1f2937', fontSize: '1.125rem' }}>
                    Guest → Free → Paid Funnel
                  </h3>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1rem'
                  }}>
                    {/* Phase 1: Anonymous */}
                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        borderRadius: '12px',
                        padding: '1.5rem',
                        color: 'white'
                      }}>
                        <div style={{ fontSize: '2rem', fontWeight: '700' }}>
                          {formatNumber(metrics.conversion_funnel?.phase1_anonymous_guests || 0)}
                        </div>
                        <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Anonymous Guests</div>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                        Phase 1
                      </div>
                    </div>

                    {/* Arrow + Rate */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.5rem', color: '#9ca3af' }}>→</div>
                      <div style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: metrics.conversion_funnel?.anon_to_free_rate_pct > 30 ? '#10b981' : '#f59e0b'
                      }}>
                        {metrics.conversion_funnel?.anon_to_free_rate_pct || 0}%
                      </div>
                    </div>

                    {/* Phase 2: Free Account */}
                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                        borderRadius: '12px',
                        padding: '1.5rem',
                        color: 'white'
                      }}>
                        <div style={{ fontSize: '2rem', fontWeight: '700' }}>
                          {formatNumber(metrics.conversion_funnel?.phase2_free_accounts || 0)}
                        </div>
                        <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Free Accounts</div>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                        Phase 2
                      </div>
                    </div>

                    {/* Arrow + Rate */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.5rem', color: '#9ca3af' }}>→</div>
                      <div style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: metrics.conversion_funnel?.free_to_paid_rate_pct > 20 ? '#10b981' : '#f59e0b'
                      }}>
                        {metrics.conversion_funnel?.free_to_paid_rate_pct || 0}%
                      </div>
                    </div>

                    {/* Phase 3: Paid */}
                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{
                        background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                        borderRadius: '12px',
                        padding: '1.5rem',
                        color: 'white'
                      }}>
                        <div style={{ fontSize: '2rem', fontWeight: '700' }}>
                          {formatNumber(metrics.conversion_funnel?.phase3_paid || 0)}
                        </div>
                        <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Paid Subscribers</div>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                        Phase 3
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detailed Metrics Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: '1rem'
                }}>
                  {/* LearningSnapshot Actions */}
                  <div style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <h4 style={{ margin: '0 0 1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                      LearningSnapshot Actions
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6b7280' }}>Create Account</span>
                        <span style={{ fontWeight: '600', color: '#10b981' }}>
                          {formatNumber(metrics.conversion_funnel?.learning_snapshot?.create_account || 0)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6b7280' }}>Continue</span>
                        <span style={{ fontWeight: '600', color: '#6366f1' }}>
                          {formatNumber(metrics.conversion_funnel?.learning_snapshot?.continue || 0)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6b7280' }}>Start Over</span>
                        <span style={{ fontWeight: '600', color: '#ef4444' }}>
                          {formatNumber(metrics.conversion_funnel?.learning_snapshot?.start_over || 0)}
                        </span>
                      </div>
                      <div style={{
                        borderTop: '1px solid #e5e7eb',
                        paddingTop: '0.75rem',
                        marginTop: '0.25rem',
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}>
                        <span style={{ color: '#6b7280' }}>Start Over Rate</span>
                        <span style={{
                          fontWeight: '600',
                          color: (metrics.conversion_funnel?.learning_snapshot?.start_over_rate_pct || 0) < 10 ? '#10b981' : '#ef4444'
                        }}>
                          {metrics.conversion_funnel?.learning_snapshot?.start_over_rate_pct || 0}%
                          {(metrics.conversion_funnel?.learning_snapshot?.start_over_rate_pct || 0) < 10 ? ' ✓' : ' (target: <10%)'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Mira Customization */}
                  <div style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <h4 style={{ margin: '0 0 1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                      Mira Customization (Investment)
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6b7280' }}>Customized Mira</span>
                        <span style={{ fontWeight: '600', color: '#8b5cf6' }}>
                          {formatNumber(metrics.conversion_funnel?.mira_customization?.customized || 0)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6b7280' }}>Customization Rate</span>
                        <span style={{
                          fontWeight: '600',
                          color: (metrics.conversion_funnel?.mira_customization?.customization_rate_pct || 0) > 50 ? '#10b981' : '#f59e0b'
                        }}>
                          {metrics.conversion_funnel?.mira_customization?.customization_rate_pct || 0}%
                        </span>
                      </div>
                    </div>
                    <div style={{
                      marginTop: '1rem',
                      padding: '0.75rem',
                      background: '#f3f4f6',
                      borderRadius: '8px',
                      fontSize: '0.75rem',
                      color: '#6b7280'
                    }}>
                      Investment psychology: Users who customize Mira are more likely to convert
                    </div>
                  </div>

                  {/* Target Metrics */}
                  <div style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <h4 style={{ margin: '0 0 1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                      Targets vs Actual
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#6b7280' }}>Anon → Free</span>
                        <div>
                          <span style={{
                            fontWeight: '600',
                            color: (metrics.conversion_funnel?.anon_to_free_rate_pct || 0) >= 35 ? '#10b981' : '#f59e0b'
                          }}>
                            {metrics.conversion_funnel?.anon_to_free_rate_pct || 0}%
                          </span>
                          <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}> / 35%</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#6b7280' }}>Free → Paid</span>
                        <div>
                          <span style={{
                            fontWeight: '600',
                            color: (metrics.conversion_funnel?.free_to_paid_rate_pct || 0) >= 20 ? '#10b981' : '#f59e0b'
                          }}>
                            {metrics.conversion_funnel?.free_to_paid_rate_pct || 0}%
                          </span>
                          <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}> / 20%</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#6b7280' }}>Start Over</span>
                        <div>
                          <span style={{
                            fontWeight: '600',
                            color: (metrics.conversion_funnel?.learning_snapshot?.start_over_rate_pct || 0) < 10 ? '#10b981' : '#ef4444'
                          }}>
                            {metrics.conversion_funnel?.learning_snapshot?.start_over_rate_pct || 0}%
                          </span>
                          <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}> / &lt;10%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* FERPA Note */}
                <div style={{
                  marginTop: '1.5rem',
                  padding: '1rem',
                  background: '#f0fdf4',
                  borderRadius: '8px',
                  border: '1px solid #bbf7d0',
                  fontSize: '0.75rem',
                  color: '#166534'
                }}>
                  <strong>FERPA Compliant:</strong> All metrics are aggregated counts. No individual student data or educational records are displayed.
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem'
              }}>
                <MetricCard title="Total Users" value={formatNumber(metrics.users?.total)} />
                <MetricCard title="New (7 days)" value={formatNumber(metrics.users?.new_last_7_days)} color="#10b981" />
                <MetricCard title="New (30 days)" value={formatNumber(metrics.users?.new_last_30_days)} color="#10b981" />
                <MetricCard title="Active Today" value={formatNumber(metrics.engagement?.active_today)} />
                <MetricCard title="Active (7 days)" value={formatNumber(metrics.engagement?.active_last_7_days)} />
                <MetricCard title="Guest Sessions" value={formatNumber(metrics.guests?.total_sessions)} />
                <MetricCard
                  title="Guest Conversion"
                  value={`${metrics.guests?.conversion_rate?.toFixed(1) || 0}%`}
                  color="#f59e0b"
                />
              </div>
            )}

            {activeTab === 'subscriptions' && (
              <div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginBottom: '2rem'
                }}>
                  <MetricCard title="Active" value={formatNumber(metrics.subscriptions?.active)} color="#10b981" />
                  <MetricCard title="Trialing" value={formatNumber(metrics.subscriptions?.trialing)} color="#f59e0b" />
                  <MetricCard title="Canceled" value={formatNumber(metrics.subscriptions?.canceled)} color="#ef4444" />
                </div>

                <h3 style={{ marginBottom: '1rem', color: '#1f2937' }}>By Plan</h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '1rem'
                }}>
                  {metrics.subscriptions?.by_plan && Object.entries(metrics.subscriptions.by_plan).map(([plan, count]) => (
                    <MetricCard key={plan} title={plan} value={formatNumber(count)} />
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'schools' && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem'
              }}>
                <MetricCard title="School Licenses" value={formatNumber(metrics.schools?.total_licenses)} color="#8b5cf6" />
                <MetricCard title="Active Licenses" value={formatNumber(metrics.schools?.active_licenses)} color="#10b981" />
                <MetricCard title="Total Seats" value={formatNumber(metrics.schools?.total_seats)} />
                <MetricCard title="Used Seats" value={formatNumber(metrics.schools?.used_seats)} />
                <MetricCard
                  title="Seat Utilization"
                  value={`${((metrics.schools?.used_seats / metrics.schools?.total_seats) * 100 || 0).toFixed(1)}%`}
                  color="#f59e0b"
                />
                <MetricCard title="Districts" value={formatNumber(metrics.schools?.districts)} />
              </div>
            )}

            {/* Generated timestamp */}
            <div style={{
              marginTop: '2rem',
              padding: '1rem',
              background: '#f9fafb',
              borderRadius: '8px',
              fontSize: '0.75rem',
              color: '#9ca3af',
              textAlign: 'center'
            }}>
              Data generated at: {metrics.generated_at || new Date().toISOString()}
              <br />
              FERPA Compliant - Aggregated metrics only, no PII
            </div>
          </>
        )}
      </main>

      {/* Spinner animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SystemAdminDashboard;
